/**
 * Wires the on-screen d-pad + action buttons (defined in index.html) to the
 * existing scene-level keydown listeners. Each button dispatches a synthetic
 * `KeyboardEvent('keydown')` on `window` carrying the `data-key` value, so
 * scenes that already listen on `window.addEventListener('keydown', ...)`
 * pick it up without modification.
 *
 * Two binding strategies, picked per-button:
 *
 * - **Action buttons (A / PKDX / ESC)** use the `click` event. `click` is the
 *   most reliable cross-browser tap signal — pointerdown + e.preventDefault()
 *   has been observed to suppress the synthetic key dispatch on some iOS
 *   Safari builds, so action buttons sidestep that path entirely. Single
 *   fire per tap is exactly the menu-confirm semantic we want.
 *
 * - **D-pad arrows** keep pointerdown + hold-to-repeat (one keydown immediately,
 *   then auto-repeat every 180ms after a 280ms grace period). Hold-to-walk
 *   needs continuous events, which `click` alone can't provide.
 *
 * The synthetic KeyboardEvent has `keyCode` and `which` mirrored from a small
 * lookup table — defensive against any listener that still reads the legacy
 * properties.
 */

const REPEAT_DELAY_MS = 280;
const REPEAT_INTERVAL_MS = 180;

const ARROW_KEYS = new Set(['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight']);

const KEY_CODES: Readonly<Record<string, number>> = {
  Enter: 13,
  Escape: 27,
  ArrowLeft: 37,
  ArrowUp: 38,
  ArrowRight: 39,
  ArrowDown: 40,
  p: 80,
  P: 80,
};

function dispatchKey(key: string): void {
  const event = new KeyboardEvent('keydown', {
    key,
    code: key === ' ' ? 'Space' : key.length === 1 ? `Key${key.toUpperCase()}` : key,
    bubbles: true,
    cancelable: true,
    composed: true,
  });
  // KeyboardEvent constructor ignores keyCode/which; backfill via defineProperty
  // for any legacy listener that still reads them.
  const code = KEY_CODES[key] ?? 0;
  Object.defineProperty(event, 'keyCode', { get: () => code });
  Object.defineProperty(event, 'which', { get: () => code });
  window.dispatchEvent(event);
}

function attachActionButton(el: HTMLElement, key: string): void {
  // `click` fires reliably on tap (touch) and click (mouse) across browsers,
  // including iOS Safari where pointerdown-based dispatch occasionally drops.
  el.addEventListener('click', (e) => {
    e.preventDefault();
    el.classList.add('pressed');
    dispatchKey(key);
    // Visual flash — clears shortly after so consecutive taps each animate.
    window.setTimeout(() => el.classList.remove('pressed'), 100);
  });
  el.addEventListener('contextmenu', (e) => e.preventDefault());
}

function attachDpadButton(el: HTMLElement, key: string): void {
  let repeatStartTimer: number | null = null;
  let repeatInterval: number | null = null;
  let pressed = false;

  const press = (e: PointerEvent) => {
    e.preventDefault();
    if (pressed) return;
    pressed = true;
    el.classList.add('pressed');
    dispatchKey(key);
    repeatStartTimer = window.setTimeout(() => {
      repeatInterval = window.setInterval(() => dispatchKey(key), REPEAT_INTERVAL_MS);
    }, REPEAT_DELAY_MS);
  };

  const release = () => {
    if (!pressed) return;
    pressed = false;
    el.classList.remove('pressed');
    if (repeatStartTimer !== null) {
      clearTimeout(repeatStartTimer);
      repeatStartTimer = null;
    }
    if (repeatInterval !== null) {
      clearInterval(repeatInterval);
      repeatInterval = null;
    }
  };

  el.addEventListener('pointerdown', press);
  el.addEventListener('pointerup', release);
  el.addEventListener('pointercancel', release);
  el.addEventListener('pointerleave', release);
  el.addEventListener('contextmenu', (e) => e.preventDefault());
}

function attachButton(el: HTMLElement): void {
  const key = el.dataset['key'];
  if (!key) return;
  if (ARROW_KEYS.has(key)) {
    attachDpadButton(el, key);
  } else {
    attachActionButton(el, key);
  }
}

/**
 * Mount the touch controls layer + bind events. Idempotent — safe to call once
 * per game boot. Caller is responsible for showing/hiding the `#controls`
 * container by toggling the `active` class.
 */
export function mountTouchControls(): void {
  const buttons = document.querySelectorAll<HTMLElement>('#controls .ctrl-btn');
  buttons.forEach(attachButton);
}
